import { Pagination } from '@mui/material';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { sxPaginationContrast } from '../../../assets/styles/CommonSxProps';
import AudioLoadingDescription from '../../../components/Accessibility/AudioLoadingDescription';
import CircularProgressLoading from '../../../components/Accessibility/CircularProgressLoading';
import { Agendamento } from '../../../interfaces/entities/Agendamento';
import { getAgendamentos } from '../../../services/AgendamentoService';
import { scrollTo } from '../../../utils/ScrollUtil';
import AdviceContainer from './AdviceContainer';
import ModalCancelarAtendimento from './ModalCancelarAtendimento';
import ModalConfirmarAgendamento from './ModalConfirmarAgendamento';
import ScheduleCard from './ScheduleCard';
import SchedulesStatusFilter from './SchedulesStatusFilter';
import './styles.css';
import { sxPagination } from './sxProps';
import PageTitle from '../../../components/Accessibility/PageTitle';

const MeusAgendamentos: React.FC = () => {

    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState(1);
    const [schedules, setSchedules] = useState<Agendamento[]>([]);
    const [showModalConfirmarAgendamento, setShowModalConfirmarAgendamento] = useState<boolean>(false);
    const [showModalCancelarAtendimento, setShowModalCancelarAtendimento] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState('todos');
    const [selectedSchedule, setSelectedSchedule] = useState<Agendamento | null>(null);

    const cardsRef = useRef<HTMLDivElement>(null);

    const accessibility = useAccessibility();

    const fetchSchedules = async () => {
        setLoading(true);
        const result = await getAgendamentos();
        if (result !== null) {
            let validTipoAtendSchedules = filterTipoAtend(result);
            setSchedules(validTipoAtendSchedules);
        } else {
            setSchedules([]);
            console.error('Falha ao buscar agendamentos.');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchSchedules();

        const threeMinutesRefresh = 3 * 60 * 1000;
        const intervalId = setInterval(() => {
            fetchSchedules();
        }, threeMinutesRefresh);

        return () => clearInterval(intervalId);
    }, [])

    const handleStatusFilterChange = (newStatus: string) => {
        setStatusFilter(newStatus);
        setPage(1); //Retorna para a primeira página da exibição sempre que um filtro for aplicado
    };

    const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
        scrollTo('schedules-container');
        cardsRef?.current?.focus();
    };

    const filterTipoAtend = (schedules: Agendamento[]) => {
        return schedules.filter((schedule) => {
            return schedule.tipoAtend !== 'PA VIRTUAL' && schedule.tipoAtend !== 'TELEORIENTACAO VIDEO' && schedule.tipoAtend !== 'TELEORIENTACAO TELEFONE';
        });
    }

    const statusFilteredSchedules = schedules.filter((schedule) => {
        return statusFilter === 'todos' || schedule.status === statusFilter;
    });

    const paginatedSchedules = statusFilteredSchedules.slice(
        (page - 1) * 5,
        page * 5
    );

    const handleClickConfirmarAgendamento = (schedule: Agendamento | null) => {
        setSelectedSchedule(schedule);
        setShowModalConfirmarAgendamento(showModalConfirmarAgendamento => !showModalConfirmarAgendamento);
    }

    const handleClickCancelarAtendimento = (schedule: Agendamento | null) => {
        setSelectedSchedule(schedule);
        setShowModalCancelarAtendimento(showModalCancelarAtendimento => !showModalCancelarAtendimento);
    }

    return (
        <div className='schedules-container' id='schedules-container' aria-atomic>
            <AudioLoadingDescription loading={loading} />
            <PageTitle text="Consultar meus agendamentos" marginBottom="24px" />
            <AdviceContainer />

            {!loading && (schedules.length > 0) && <SchedulesStatusFilter statusFilter={statusFilter} onStatusChange={handleStatusFilterChange} />}

            <div className='schedule-cards-holder' tabIndex={-1} ref={cardsRef}>
                {loading &&
                    <CircularProgressLoading />
                }
                {!loading && statusFilteredSchedules.length === 0 &&
                    <h1 className='empty-schedules' style={accessibility.textStyleAccessibility} tabIndex={0}>Nenhum agendamento encontrado.</h1>
                }
                {!loading && statusFilteredSchedules.length > 0 && (paginatedSchedules.map((schedule) => (
                    <ScheduleCard
                        agendamento={schedule}
                        key={schedule.id}
                        handleClickCancelarAtendimento={() => handleClickCancelarAtendimento(schedule)}
                        handleClickConfirmarAgendamento={() => handleClickConfirmarAgendamento(schedule)}
                    />
                )))}
            </div>

            {statusFilteredSchedules.length > 5 && !loading &&
                <div className='pagination-holder'>
                    <Pagination
                        count={Math.ceil(statusFilteredSchedules.length / 5)}
                        sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination} hideNextButton hidePrevButton
                        color='primary'
                        page={page}
                        onChange={handlePageChange}
                    />
                </div>
            }
            <ModalConfirmarAgendamento
                handleShowModal={() => handleClickConfirmarAgendamento(null)}
                showModal={showModalConfirmarAgendamento}
                agendamento={selectedSchedule}
                onComplete={fetchSchedules}
            />
            <ModalCancelarAtendimento
                handleShowModal={() => handleClickCancelarAtendimento(null)}
                showModal={showModalCancelarAtendimento}
                agendamento={selectedSchedule}
                onComplete={fetchSchedules}
            />
        </div>
    )
}

export default MeusAgendamentos;